'use client'

import type Lenis from 'lenis'
import { useLenis } from 'lenis/react'
import { useCallback, useEffect, useState } from 'react'

import {
  getEffectiveHeroScrollY,
  nextHeroScrollCompactState,
} from '@/components/Hero/heroStickyBannerScrollCompact'
import {
  getPageHeroBottomContentClassName,
  type PageHeroStickyBannerClearanceVariant,
} from '@/components/Hero/pageHeroLayout'
import { useSitePromoBannerVisible } from '@/components/PromotionBanner/SitePromoBannerVisibility'

export function useHeroStickyBannerClearance(variant: PageHeroStickyBannerClearanceVariant) {
  const lenis = useLenis()
  const bannerVisible = useSitePromoBannerVisible()
  const [scrollCompact, setScrollCompact] = useState(false)
  const [scrollReady, setScrollReady] = useState(false)
  const [animatePadding, setAnimatePadding] = useState(true)
  const compact = (scrollReady && scrollCompact) || !bannerVisible

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      setScrollReady(true)
    })

    return () => {
      window.cancelAnimationFrame(id)
    }
  }, [])

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncMotionPreference = () => {
      setAnimatePadding(!media.matches)
    }

    syncMotionPreference()
    media.addEventListener('change', syncMotionPreference)

    return () => {
      media.removeEventListener('change', syncMotionPreference)
    }
  }, [])

  const updateClearance = useCallback((lenisInstance: Lenis | null | undefined) => {
    const scrollY = getEffectiveHeroScrollY(
      lenisInstance?.scroll,
      window.scrollY,
      document.documentElement.scrollTop,
    )
    setScrollCompact((current) => nextHeroScrollCompactState(current, scrollY))
  }, [])

  useEffect(() => {
    let settleRafId = 0
    let settleTimeoutId = 0

    const syncClearance = () => {
      updateClearance(lenis)
    }

    const syncClearanceAfterSettle = () => {
      window.cancelAnimationFrame(settleRafId)
      window.clearTimeout(settleTimeoutId)
      settleRafId = window.requestAnimationFrame(() => {
        syncClearance()
        settleTimeoutId = window.setTimeout(syncClearance, 80)
      })
    }

    syncClearance()

    if (lenis) {
      lenis.on('scroll', syncClearance)
      lenis.on('scrollend', syncClearanceAfterSettle)
    }

    window.addEventListener('scroll', syncClearance, { passive: true })
    window.addEventListener('scrollend', syncClearanceAfterSettle, { passive: true })
    window.addEventListener('touchend', syncClearanceAfterSettle, { passive: true })

    return () => {
      window.cancelAnimationFrame(settleRafId)
      window.clearTimeout(settleTimeoutId)
      if (lenis) {
        lenis.off('scroll', syncClearance)
        lenis.off('scrollend', syncClearanceAfterSettle)
      }
      window.removeEventListener('scroll', syncClearance)
      window.removeEventListener('scrollend', syncClearanceAfterSettle)
      window.removeEventListener('touchend', syncClearanceAfterSettle)
    }
  }, [lenis, updateClearance])

  const contentClassName = getPageHeroBottomContentClassName({
    animatePadding,
    compact,
    variant,
  })

  return { compact, contentClassName }
}
